import { useMemo } from 'react';
import { useMatch } from 'react-router';
import { AppRoutes, TECHNOLOGY_SUPPORT_EMAIL } from '@/constants';
import { selectChatId, selectChatMatterInfo, selectUserAboutData, useAppSelector } from '@/store';
import { buildSupportComposeHref } from '@/utils';

/**
 * Outlook web compose URLs for tech support. The body includes user info, plus the current chat
 * only on a chat page. Not `mailto:` — on Windows it's easily bound to a browser and opens a blank
 * tab.
 */
export const useSupportComposeHrefs = () => {
  const userAboutData = useAppSelector(selectUserAboutData);
  const storeChatId = useAppSelector(selectChatId);
  const chatMatterInfo = useAppSelector(selectChatMatterInfo);
  // Include chat details only on `/chat/:chatId`.
  const chatRouteMatch = useMatch(`/${AppRoutes.Chat}/:chatId`);
  const chatIdFromUrl = chatRouteMatch?.params.chatId;

  return useMemo(() => {
    const diagnostics: string[] = [];

    if (userAboutData) {
      diagnostics.push(
        `User ID: ${userAboutData.userId}`,
        `Name: ${userAboutData.name}`,
        `Organisation ID: ${userAboutData.organisationId}`,
      );
    }

    // And only when the store's chat matches the URL, to avoid the previous chat during navigation.
    if (chatIdFromUrl && chatIdFromUrl === storeChatId) {
      diagnostics.push(`Chat ID: ${chatIdFromUrl}`, `Chat URL: ${window.location.href}`);
      if (chatMatterInfo?.matterId) diagnostics.push(`Matter ID: ${chatMatterInfo.matterId}`);
      if (chatMatterInfo?.matterReference) diagnostics.push(`Matter Reference: ${chatMatterInfo.matterReference}`);
    }

    return {
      reportIssueHref: buildSupportComposeHref({
        to: TECHNOLOGY_SUPPORT_EMAIL,
        subject: '[Fletch] I have found an issue',
        intro: 'Please explain your issue below:',
        diagnostics,
      }),
      suggestImprovementHref: buildSupportComposeHref({
        to: TECHNOLOGY_SUPPORT_EMAIL,
        subject: '[Fletch] I have a suggestion',
        intro: 'Please describe your suggestion below:',
        diagnostics,
      }),
    };
  }, [userAboutData, storeChatId, chatIdFromUrl, chatMatterInfo]);
};
